import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import Loading from '../../components/process-state-notifications/Loading'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { useProductBacklogGridOptions } from './gridOptions'
import { useProjectPrivateContext } from '../../context/projectContext'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { useProductBacklogData } from './hooks/productBacklogData'
import {
  ColDef,
  ColumnApi,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  RowDataUpdatedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
  isSprintRow,
  findDuplicateAssignments,
} from './productBacklog'
import store, { AllState } from '../../../store'
import { connect, useDispatch, useSelector } from 'react-redux'
import { FunctionLayer } from '../../../store/functionLayer'
import { TeamSelector } from './Header/TeamSelector'
import { useWbsItemSelectColumnOptions } from '../../hooks/useWbsItemSelectColumnOptions'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import {
  addGlobalMessage,
  addScreenMessage,
  MessageLevel,
} from '../../../store/messages'
import { useIntl } from 'react-intl'
import {
  RestoreType,
  useBulkSheetState,
} from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { getSelectedNode } from '../../containers/BulkSheetView/lib/gridApi'
import {
  addCopyURLAndNameMenuItems,
  addCopyURLMenuItems,
  collapseAllRows,
  expandAllRows,
} from '../../containers/BulkSheetView/gridOptions/contextMenu'
import { usePageState } from '../../hooks/usePageState'
import { WorkloadUnit } from '../../../lib/functions/workload'
import { UserBasic } from '../../../lib/functions/user'
import { DISPLAY_DATE_SHORT_FORMAT_WITH_DAY } from '../../../utils/date'
import { ROW_HEIGHT } from '../../containers/BulkSheet'
import { useDialog } from './hooks/dialog'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { useExcel } from './hooks/excel'
import ProductBacklogWbsHeader from './Header/ProductBacklogWbsHeader'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { ExcelExportDialog } from '../../containers/BulkSheet/ExcelExportDialog'
import { useWorkloadUnit } from '../../hooks/useWorkloadUnit'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { SprintDetail } from '../../../lib/functions/sprint'
import {
  setSprintMenuItems,
  removeFromSprintMenuItems,
  addSprintMenuItem,
  sprintInteractiveAdjustmentMenuItem,
} from './gridOptions/contextMenu'
import { StatusChangePopper } from '../../components/poppers/StatusChangePopper'
import { requireSave } from '../../../store/requiredSaveData'
import {
  FIELD_WBSITEM_ESTIMATED_WORKLOAD_DELIVERABLE,
  FIELD_WBSITEM_ESTIMATED_WORKLOAD_DELIVERABLE_PER_SPRINT,
  FIELD_WBSITEM_SPRINTS,
  FIELD_WBSITEM_STATUS,
} from './gridOptions/columnDefs'
import { useSprintManager } from './hooks/sprintManager'
import { useProductBacklogDragAndDrop } from './hooks/dragAndDrop'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import { SavedUIState } from '../../components/dialogs/SavedUIStateDialog/SavedUIStateList'
import { EstimatesByAssigneePopup } from './components/EstimatesByAssigneePopup'
import { useEstimatesByAssignee } from './hooks/estimatesByAssignee'
import {
  SprintAssignmentDialog,
  SpiAdjustmentItem,
} from './components/SprintAssignmentDialog'
import { useSprintAdjustment } from './hooks/useSprintAdjustment'
import { useEstimateVarianceHandler } from './hooks/estimateVariance'

const EXCEL_EXPORT_EXCLUDE_COLIDS: string[] = ['uuid']

const mapStateToProps = (state: AllState) => ({
  functionLayers: state.functionLayer.layers,
})

type ProductBacklogPageState = {
  teamUuid?: string
  workloadUnit: WorkloadUnit
  dateFormat: string
  rowHeight: number
  hideFinishedSprints?: boolean
}

type StateProps = {
  functionLayers: Immutable.Map<number, FunctionLayer>
}
type WrapperProps = PageProps & StateProps

export const ProductBacklog = connect(mapStateToProps)(
  projectPrivate(
    ({ uuid: functionUuid, functionLayers: layers }: WrapperProps) => {
      const dispatch = useDispatch()
      const intl = useIntl()
      const [loading, setLoading] = useState<boolean>(false)
      const { projectUuid, project } = useProjectPrivateContext()
      const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
      const [sortColumnsState, setSortColumnsState] = useState<
        SortedColumnState[]
      >([])
      const ref = useRef<HTMLDivElement>(null)
      const [teamUuid, setTeamUuid] = useState<string>()
      const {
        fetchedSelectColumnOptions: fetchedWbsItemSelectColumnOptions,
        selectColumnOptions: wbsItemSelectColumnOptions,
      } = useWbsItemSelectColumnOptions({
        projectUuid,
        targetColumnOptionTypes: [
          'STATUS',
          'SUBSTATUS',
          'PRIORITY',
          'DIFFICULTY',
          'TEAM',
          'MEMBER',
          'SPRINT',
          'TAG',
        ],
      })
      // sprintが変更されるたび更新したい
      const [sprints, setSprints] = useState<SprintDetail[]>([])
      const [sprintsFetched, setSprintsFetched] = useState<boolean>(false)

      useEffect(() => {
        if (wbsItemSelectColumnOptions && teamUuid) {
          try {
            // 選択されたチームに紐づいたスプリントのみをフィルタリング
            const allSprints = wbsItemSelectColumnOptions[
              'sprint'
            ] as SprintDetail[]
            const teamSprints =
              allSprints?.filter(sprint => sprint?.teamUuid === teamUuid) || []
            setSprints(teamSprints)
            setSprintsFetched(true)
          } catch (error) {
            setSprints([])
            setSprintsFetched(true)
          }
        } else if (teamUuid && fetchedWbsItemSelectColumnOptions) {
          setSprints([])
          setSprintsFetched(true)
        } else {
          setSprintsFetched(false)
        }
      }, [
        wbsItemSelectColumnOptions,
        teamUuid,
        fetchedWbsItemSelectColumnOptions,
      ])

      const {
        data,
        setData,
        refresh,
        save,
        refreshSingleRow,
        saveSingleRow,
        isLoading,
      } = useProductBacklogData(
        projectUuid,
        teamUuid && sprintsFetched ? teamUuid : undefined,
        sprints
      )

      // Use ref to avoid re-rendering others depends on following functions.
      const refreshSingleRowRef = useRef<Function>()
      refreshSingleRowRef.current = refreshSingleRow
      const saveSingleRowRef = useRef<Function>()
      saveSingleRowRef.current = saveSingleRow

      const dialog = useDialog()
      const columnSetting = useColumnSetting()
      const onClickedStatusCell = useCallback(
        (
          target: EventTarget | undefined,
          row: ProductBacklogItemRow | undefined
        ) => {
          const getAgCellElement = (elem: any): any => {
            if (!elem || elem.className.toString().includes('sevend-ag-cell')) {
              return elem
            }
            return getAgCellElement(elem.parentElement)
          }
          const targetElement = getAgCellElement(target)
          if (!target || !row?.wbsItem) return
          dialog.openStatusPopper(targetElement, row.wbsItem, {})
        },
        [dialog]
      )

      const reloadSingleRow = useCallback((wbsItemUuid: string | undefined) => {
        if (!refreshSingleRowRef.current) return
        refreshSingleRowRef.current(wbsItemUuid)

        if (gridApi.current) {
          let existsEditedRow: boolean = false
          gridApi.current.forEachNode(
            (row: IRowNode<ProductBacklogItemRow>) => {
              if (row.data?.edited) existsEditedRow = true
            }
          )
          if (existsEditedRow) store.dispatch(requireSave())
        }
      }, [])

      const submitSingleRow = useCallback(async (wbsItemUuid: string) => {
        if (saveSingleRowRef?.current) {
          await saveSingleRowRef.current(wbsItemUuid)
          refreshSingleRowRef?.current?.(wbsItemUuid)
        }
      }, [])

      // データの変更を追跡するためのバージョン番号（ポップアップの再計算トリガー）
      const [dataVersion, setDataVersion] = useState<number>(0)
      const incrementDataVersion = useCallback(() => {
        setDataVersion(prev => prev + 1)
      }, [])

      // 工数換算基準を取得
      const workloadStandard = useSelector((state: AllState) => ({
        dailyWorkHours: state.tenant.organization?.dailyWorkHours || 8,
        monthlyWorkDays: state.tenant.organization?.monthlyWorkDays || 20,
      }))

      // gridApiを先に宣言（useEstimatesByAssigneeで使用するため）
      const gridApi = useRef<GridApi>()

      const {
        estimatesPopupOpen,
        openEstimatesPopup,
        openEstimatesPopupForSprint,
        closeEstimatesPopup,
        getSprintEstimates,
        initialSprintId,
      } = useEstimatesByAssignee(data, sprints, gridApi.current)

      // スプリントインタラクティブ調整ダイアログ
      const [sprintAdjustmentDialog, setSprintAdjustmentDialog] = useState<{
        open: boolean
        deliverable?: ProductBacklogItemRow
      }>({ open: false })

      const openSprintAdjustmentDialog = useCallback(
        (deliverable: ProductBacklogItemRow) => {
          setSprintAdjustmentDialog({ open: true, deliverable })
        },
        []
      )

      const closeSprintAdjustmentDialog = useCallback(() => {
        setSprintAdjustmentDialog({ open: false })
      }, [])

      const { applySprintAdjustment } = useSprintAdjustment({
        sprints,
        data,
        setData,
        incrementDataVersion,
      })

      const handleSprintAdjustmentApply = useCallback(
        (items: SpiAdjustmentItem[]) => {
          if (!sprintAdjustmentDialog.deliverable) return undefined
          const result = applySprintAdjustment(
            sprintAdjustmentDialog.deliverable,
            items
          )
          // グリッドの行データを明示的に更新
          if (result) {
            gridApi.current?.setRowData(result)
          }
          return result
        },
        [sprintAdjustmentDialog.deliverable, applySprintAdjustment]
      )

      const gridOptions = useProductBacklogGridOptions({
        projectUuid,
        projectCode: project.code,
        reloadSingleRow,
        submitSingleRow,
        onClickedStatusCell,
        onClickSprintEstimates: openEstimatesPopupForSprint,
        selectColumnOptions: wbsItemSelectColumnOptions,
        incrementDataVersion,
        openSprintAdjustmentDialog,
      })
      const gridOptionsRef = useRef<GridOptions>()
      const columnApi = useRef<ColumnApi>()

      const defaultPageState = useMemo<ProductBacklogPageState>(
        () => ({
          workloadUnit: WorkloadUnit.HOUR,
          dateFormat: DISPLAY_DATE_SHORT_FORMAT_WITH_DAY,
          rowHeight: ROW_HEIGHT.SMALL,
        }),
        []
      )

      const onExcelExport = useExcel(gridOptions)
      const {
        teamUuid: teamUuidInPageState,
        workloadUnit,
        dateFormat,
        rowHeight,
        updatePageState,
      } = usePageState<ProductBacklogPageState>(functionUuid, defaultPageState)

      const restoreTypes = useMemo<RestoreType[]>(
        () => ['COLUMN_STATE', 'FILTER_STATE'],
        []
      )
      const bulkSheetState = useBulkSheetState(
        functionUuid,
        projectUuid,
        UiStateKey.ProductBacklogColumnAndFilterState,
        restoreTypes,
        projectUuid
      )

      useEffect(() => {
        if (teamUuidInPageState) {
          setTeamUuid(teamUuidInPageState)
        }
      }, [teamUuidInPageState])

      const onGridReady = useCallback(
        (event: GridReadyEvent<ProductBacklogItemRow>) => {
          gridOptionsRef.current = gridOptions
          gridApi.current = event.api
          columnApi.current = event.columnApi
        },
        [gridOptions]
      )
      const onFirstDataRendered = useCallback(
        (event: FirstDataRenderedEvent) => {
          if (!_.isEmpty(bulkSheetState.filterState)) {
            gridApi.current?.setFilterModel(bulkSheetState.filterState)
          }
          if (!_.isEmpty(bulkSheetState.columnState)) {
            event.columnApi.applyColumnState({
              state: bulkSheetState.columnState,
              applyOrder: true,
            })
          }
          bulkSheetState.finishRestoring()
          setLoading(false)
        },
        [bulkSheetState]
      )
      const onRowDataUpdated = useCallback(
        (e: RowDataUpdatedEvent<ProductBacklogRow>) => {
          e.api?.refreshCells({
            columns: [
              'rowNumber',
              FIELD_WBSITEM_ESTIMATED_WORKLOAD_DELIVERABLE,
              FIELD_WBSITEM_ESTIMATED_WORKLOAD_DELIVERABLE_PER_SPRINT,
              FIELD_WBSITEM_SPRINTS,
              FIELD_WBSITEM_STATUS,
            ],
            force: true,
          })
        },
        []
      )

      const onCellValueChanged = useEstimateVarianceHandler()

      const { setSprint, switchSprint, removeFromParentSprintRow, addSprint } =
        useSprintManager(data, setData, sprints)
      const { onRowDragEnter, onRowDragMove, onRowDragEnd, onRowDragLeave } =
        useProductBacklogDragAndDrop(
          ref,
          gridOptions,
          data,
          setData,
          switchSprint
        )

      // BulksSheetView Actions ---------------

      const onFilterChanged = useCallback(
        (e: FilterChangedEvent) => {
          const filterModel = e.api?.getFilterModel()
          delete filterModel['uuid']
          bulkSheetState.saveFilterState(filterModel)
          setFilteredColumns(
            Object.keys(filterModel)
              .map(col => e.api.getColumnDef(col))
              .filter(v => !!v && v.field !== 'uuid') as ColDef[]
          )
          // フィルター変更時に見積もりポップアップを再計算
          incrementDataVersion()
          // フィルター変更時にスプリント行の集計セルを再描画
          e.api.refreshCells({ force: true })
        },
        [bulkSheetState, incrementDataVersion]
      )

      const rememberColumnState = useCallback(() => {
        const columnState = columnApi.current?.getColumnState()
        columnState && bulkSheetState.saveColumnState(columnState)
      }, [bulkSheetState])

      const onSortedColumnsStateChanged = useCallback((e: SortChangedEvent) => {
        const sortedColumns = e.columnApi
          .getColumnState()
          .filter(col => !!col.sort)
          .map(col => {
            return {
              ...e.api.getColumnDef(col.colId),
              colId: col.colId,
            }
          })
          .filter(v => !!v) as ColDef[]
        e.api.setSuppressRowDrag(0 < sortedColumns.length)

        const columnState = columnApi.current?.getColumnState()
        const sortedColumnState: { [colId: string]: ColumnState } = {}
        columnState &&
          columnState.forEach(state => {
            if (state.sort) {
              sortedColumnState[state.colId] = state
            }
          })
        const sortedColumnsState: SortedColumnState[] = sortedColumns.map(
          col => {
            return {
              colId: col.colId,
              field: col.field,
              headerName: col.headerName,
              sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
            }
          }
        )

        setSortColumnsState(sortedColumnsState)
      }, [])

      const onColumnVisible = useCallback(
        (e: ColumnVisibleEvent) => {
          rememberColumnState()
          onSortedColumnsStateChanged(e)
        },
        [rememberColumnState, onSortedColumnsStateChanged]
      )

      const onSortChanged = useCallback(
        (e: SortChangedEvent) => {
          rememberColumnState()
          onSortedColumnsStateChanged(e)
        },
        [rememberColumnState, onSortedColumnsStateChanged]
      )

      const onSubmit = useCallback(async () => {
        if (!gridApi?.current) return
        setLoading(true)
        try {
          gridApi.current?.stopEditing()
          if (gridOptions.context.errors?.hasMessage()) {
            dispatch(
              addGlobalMessage({
                type: MessageLevel.WARN,
                title: intl.formatMessage({
                  id: 'global.warning.businessError',
                }),
                text: gridOptions.context.errors.toMessage((id: any) => {
                  return data.findIndex(d => d.uuid === id) + 1
                }),
              })
            )
            return
          }

          // 同一成果物・同一スプリント・同一担当者の重複チェック
          const duplicateRowIndices = findDuplicateAssignments(data)
          if (duplicateRowIndices.length > 0) {
            const errorMessage = duplicateRowIndices
              .map(
                rowNum =>
                  `#${rowNum} > ${intl.formatMessage({ id: 'productBacklog.error.duplicateAssignment' })}`
              )
              .join('\n')
            dispatch(
              addGlobalMessage({
                type: MessageLevel.WARN,
                title: intl.formatMessage({
                  id: 'global.warning.businessError',
                }),
                text: errorMessage,
              })
            )
            return
          }

          bulkSheetState.saveImmediately()

          // Save
          await save()

          dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.SUCCESS,
              title: intl.formatMessage({ id: 'registration.complete' }),
            })
          )
          await refresh()
        } finally {
          setLoading(false)
        }
      }, [
        functionUuid,
        gridOptions.context.errors,
        bulkSheetState,
        save,
        refresh,
        dispatch,
        intl,
        data,
      ])

      useEffect(() => {
        if (gridOptions && wbsItemSelectColumnOptions) {
          gridOptions.context = {
            ...gridOptions.context,
            ...wbsItemSelectColumnOptions,
          }
        }
      }, [gridOptions, wbsItemSelectColumnOptions])

      const initializeColumnState = useCallback(() => {
        columnApi.current?.resetColumnState()
        const filterModel = gridApi.current?.getFilterModel()
        const quick = filterModel?.['uuid']
        gridApi.current?.setFilterModel(quick ? { uuid: quick } : null)
      }, [])

      const workloadUnitState = useWorkloadUnit(workloadUnit)
      useEffect(() => {
        gridOptions.context = {
          ...gridOptions.context,
          workloadUnit,
          dateFormat,
          workloadUnitState,
        }
        gridApi.current?.refreshCells({ force: true })
      }, [gridOptions, workloadUnit, dateFormat, workloadUnitState])

      useEffect(() => {
        gridApi.current?.resetRowHeights()
      }, [rowHeight])

      // Key bind ------
      useKeyBind(
        [
          {
            key: KEY_SAVE,
            fn: onSubmit,
            stopDefaultBehavior: true,
          },
        ],
        [onSubmit]
      )

      const contextMenu = useCallback(
        (params: GetContextMenuItemsParams<ProductBacklogItemRow>) => {
          if (!params.node) return []
          const selectedNodes = getSelectedNode(params.api)
          if (selectedNodes.some(v => v.data && isSprintRow(v.data))) {
            return [expandAllRows(params), collapseAllRows(params)]
          }
          const selectedWbsItems = selectedNodes
            .filter(v => v.data && isProductBacklogItemRow(v.data))
            .map(node => node.data?.wbsItem)
          const menuItems = [
            addCopyURLMenuItems(selectedWbsItems),
            addCopyURLAndNameMenuItems(selectedWbsItems),
            sprintInteractiveAdjustmentMenuItem(
              params,
              openSprintAdjustmentDialog
            ),
            setSprintMenuItems(teamUuid, sprints, setSprint, params),
            removeFromSprintMenuItems(removeFromParentSprintRow, params),
            addSprintMenuItem(teamUuid, sprints, addSprint, params),
            expandAllRows(params),
            collapseAllRows(params),
          ]
          return menuItems.filter(item => item !== null)
        },
        [
          teamUuid,
          sprints,
          setSprint,
          removeFromParentSprintRow,
          addSprint,
          openSprintAdjustmentDialog,
        ]
      )

      // Header Actions ---------------
      const onChangeTeam = useCallback(
        (teamUuid: string | undefined) => {
          setTeamUuid(teamUuid)
          updatePageState({ teamUuid })
        },
        [updatePageState]
      )
      const onChangeWorkloadUnit = useCallback(
        (workloadUnit: WorkloadUnit) => {
          updatePageState({ workloadUnit })
        },
        [updatePageState]
      )

      const hasChanged = useCallback((): boolean => {
        return data.some(v => v.edited)
      }, [data])
      const onReload = useCallback(async () => {
        if (hasChanged()) {
          dialog.openCancelConfirmation()
        } else {
          setLoading(true)
          await refresh()
          setLoading(false)
        }
      }, [dialog, hasChanged, refresh])

      const onDeleteFilteredColumn = useCallback((column: ColDef) => {
        if (!column || !gridApi.current) return
        const filterModel = gridApi.current?.getFilterModel()
        delete filterModel[column.colId || column.field || '']
        gridApi.current?.setFilterModel(filterModel)
      }, [])

      const resetFilters = useCallback(() => {
        gridApi.current?.setFilterModel([])
      }, [])

      const onDeleteSortedColumn = useCallback(
        (colId: string | ColDef<ProductBacklogItemRow>) => {
          columnApi.current?.applyColumnState({
            state: [{ colId: colId.toString(), sort: null }],
          })
        },
        []
      )

      const onDeleteSortedAllColumns = useCallback(() => {
        columnApi.current?.applyColumnState({
          defaultState: { sort: null },
        })
      }, [])

      const onChangeSortColumnState = useCallback(
        (
          colId: string | ColDef<ProductBacklogItemRow>,
          sort: 'asc' | 'desc' | null
        ) => {
          columnApi.current?.applyColumnState({
            state: [{ colId: colId.toString(), sort }],
          })
        },
        []
      )

      const onChangeRowHeight = useCallback(
        (rowHeight: number) => {
          updatePageState({ rowHeight })
        },
        [updatePageState]
      )

      const onChangeDateFormat = useCallback(
        (dateFormat: string) => {
          updatePageState({ dateFormat })
        },
        [updatePageState]
      )

      return (
        <PageArea>
          {layers.size === 1 && (
            <SavePopper loading={loading} onSubmit={onSubmit} />
          )}
          <TeamSelector teamUuid={teamUuid} setTeamUuid={onChangeTeam} />
          <ProductBacklogWbsHeader
            workloadUnit={workloadUnit}
            onChangeWorkloadUnit={onChangeWorkloadUnit}
            onCancel={onReload}
            isLoading={loading}
            filteredColumns={filteredColumns}
            onDeleteFilteredColumn={onDeleteFilteredColumn}
            resetFilters={resetFilters}
            sortColumnsState={sortColumnsState}
            onDeleteSortedColumn={onDeleteSortedColumn}
            onDeleteSortedAllColumns={onDeleteSortedAllColumns}
            onChangeSortColumnState={onChangeSortColumnState}
            rowHeight={rowHeight}
            onChangeHeight={onChangeRowHeight}
            onClickExportExcel={dialog.openExcelExport}
            onClickFavoriteColumnFilterButton={dialog.openUiState}
            onClickColumnSettingButton={columnSetting.toggle}
            columnSettingOpen={columnSetting.isOpen}
            currentFormat={dateFormat}
            onChangeDateFormat={onChangeDateFormat}
            gridApi={gridApi.current}
            onClickEstimatesByAssignee={openEstimatesPopup}
          />
          <BulkSheetView
            ref={ref}
            gridOptions={gridOptions}
            rowData={data}
            getContextMenuItems={contextMenu}
            rowHeight={rowHeight}
            // Events and actions
            onColumnVisible={onColumnVisible}
            onColumnResized={rememberColumnState}
            onColumnMoved={rememberColumnState}
            onFilterChanged={onFilterChanged}
            onSortChanged={onSortChanged}
            onRowDataUpdated={onRowDataUpdated}
            onCellValueChanged={onCellValueChanged}
            onRowDragEnter={onRowDragEnter}
            onRowDragMove={onRowDragMove}
            onRowDragEnd={onRowDragEnd}
            onRowDragLeave={onRowDragLeave}
            onFirstDataRendered={onFirstDataRendered}
            onGridReady={onGridReady}
          />
          <Loading isLoading={loading} elem={ref.current} />
          <StatusChangePopper
            anchorEl={dialog.statusPopperParams.anchorEl}
            projectUuid={projectUuid}
            wbsItem={dialog.statusPopperParams.wbsItem}
            wbsItemDelta={dialog.statusPopperParams.wbsItemDelta}
            onClose={dialog.closeStatusPopper}
            onAfterUpdate={async () => {
              const wbsItemUuid = dialog.statusPopperParams.wbsItem?.uuid
              const currentRow = data.find(
                row =>
                  isProductBacklogItemRow(row) &&
                  row.wbsItem.uuid === wbsItemUuid
              )
              const parentSprintUuid =
                currentRow && isProductBacklogItemRow(currentRow)
                  ? currentRow.treeValue[0]
                  : undefined

              await refreshSingleRow(wbsItemUuid)

              // 親スプリント行の進捗表示を更新
              if (parentSprintUuid) {
                setTimeout(() => {
                  const parentSprintNode =
                    gridApi.current?.getRowNode(parentSprintUuid)
                  if (parentSprintNode) {
                    gridApi.current?.refreshCells({
                      rowNodes: [parentSprintNode],
                      columns: [FIELD_WBSITEM_STATUS],
                      force: true,
                    })
                  }
                }, 0)
              }
            }}
          />
          <ColumnSettingPopper
            anchorEl={columnSetting.anchorEl}
            open={columnSetting.isOpen}
            columnApi={columnApi.current ?? undefined}
            gridApi={gridApi.current ?? undefined}
            close={columnSetting.close}
            height={ref.current?.offsetHeight}
            openSavedUiStateDialog={dialog.openUiState}
            initializeColumnState={initializeColumnState}
            applicationFunctionUuid={functionUuid}
            uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
            columnState={bulkSheetState.columnState}
            offset={110}
          />
          {dialog.cancelConfirmation && (
            <CancelConfirmDialog
              open={true}
              onConfirm={async () => {
                dialog.closeCancelConfirmation()
                setLoading(true)
                await refresh()
                setLoading(false)
              }}
              onClose={dialog.closeCancelConfirmation}
            />
          )}
          {dialog.excelExport && (
            <ExcelExportDialog
              onClose={dialog.closeExcelExport}
              onSubmit={onExcelExport}
              columnApi={columnApi.current}
              excludeColIds={EXCEL_EXPORT_EXCLUDE_COLIDS}
            />
          )}
          {dialog.uiState && (
            <SavedUIStateDialog
              applicationFunctionUuid={functionUuid}
              open={true}
              title={intl.formatMessage({
                id: 'savedUIState.BULK_SHEET_UI_STATE_COLUMN_AND_FILTER',
              })}
              uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
              sharable={true}
              currentUIState={{
                columnState: bulkSheetState.columnState,
                filterState: bulkSheetState.filterState,
              }}
              onSelect={(uiState: SavedUIState) => {
                const { columnState, filterState } = uiState.UIState
                if (columnState) {
                  bulkSheetState.saveColumnState(columnState)
                  columnApi.current?.applyColumnState({
                    state: columnState,
                    applyOrder: true,
                  })
                }
                if (filterState) {
                  bulkSheetState.saveFilterState(filterState)
                  gridApi.current?.setFilterModel(filterState)
                }
                dialog.closeUiState()
              }}
              onClose={dialog.closeUiState}
            />
          )}
          <EstimatesByAssigneePopup
            open={estimatesPopupOpen}
            onClose={closeEstimatesPopup}
            getSprintEstimates={getSprintEstimates}
            initialSprintId={initialSprintId}
            data={data}
            dataVersion={dataVersion}
            workloadUnit={workloadUnit}
            workloadStandard={workloadStandard}
          />
          {sprintAdjustmentDialog.open &&
            sprintAdjustmentDialog.deliverable && (
              <SprintAssignmentDialog
                open={true}
                onClose={closeSprintAdjustmentDialog}
                onApply={handleSprintAdjustmentApply}
                onValidationError={(message: string) => {
                  dispatch(
                    addScreenMessage(functionUuid, {
                      type: MessageLevel.WARN,
                      title: message,
                    })
                  )
                }}
                sprints={sprints}
                deliverable={sprintAdjustmentDialog.deliverable}
                teamUuid={teamUuid}
                workloadUnit={workloadUnit}
                teamMembers={
                  wbsItemSelectColumnOptions?.member as UserBasic[] | undefined
                }
              />
            )}
        </PageArea>
      )
    }
  )
)
